import { createContext, useContext, useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { useCanvasSegments, useCanvasGroups } from './CanvasContext';
import { canvasSelectionStore } from './canvasSelectionStore';
import { resolveGroupRedirect } from '../lib/resolveGroupRedirect';
import { useCanvasContextMenus } from '../hooks/useCanvasContextMenus';

// ============================================
// Shared entity lookup data – computed ONCE per render cycle,
// NOT per entity component. Eliminates O(n) per-component hooks.
// ============================================

export interface EntityDataContextValue {
  /** Set of "entityId:side" keys that have at least one edge connected. */
  connectorHasEdge: Set<string>;
  /** Map of "entityId:side" → array of { segmentId, end } for all connected segments. */
  connectorSegmentsMap: Map<string, Array<{ segmentId: string; end: 'from' | 'to' }>>;
  /** Set of "entityId:side" keys that belong to a currently selected edge. */
  selectedEdgeConnectors: Set<string>;
  /** Returns the outermost invisible group ID for a given entity, or null. */
  getRedirectGroupId: (entityId: string) => string | null;
  /** Context menu handler for entities (called on right-click). */
  handleEntityContextMenu: (entityId: string, e: React.MouseEvent) => void;
  /** Toggle collapse state of a group. */
  handleGroupToggleCollapse: (groupId: string) => void;
}

const EMPTY_SET = new Set<string>();
const EMPTY_MAP = new Map<string, Array<{ segmentId: string; end: 'from' | 'to' }>>();
const NOOP = () => {};

const DEFAULT_VALUE: EntityDataContextValue = {
  connectorHasEdge: EMPTY_SET,
  connectorSegmentsMap: EMPTY_MAP,
  selectedEdgeConnectors: EMPTY_SET,
  getRedirectGroupId: () => null,
  handleEntityContextMenu: NOOP as any,
  handleGroupToggleCollapse: NOOP,
};

const EntityDataCtx = createContext<EntityDataContextValue>(DEFAULT_VALUE);

export function useEntityData(): EntityDataContextValue {
  return useContext(EntityDataCtx);
}

/**
 * Provider that wraps the entity rendering layer.
 * Computes connector maps, selected edge connectors, and group redirect
 * once, then all entity components read from context (O(1) per component).
 */
export function EntityDataProvider({ children }: { children: React.ReactNode }) {
  const segments = useCanvasSegments();
  const groups = useCanvasGroups();

  // ── Connector maps (replaces useCanvasConnectorMaps per component) ──
  const { connectorSegmentsMap, connectorHasEdge } = useMemo(() => {
    const map = new Map<string, Array<{ segmentId: string; end: 'from' | 'to' }>>();
    function addKey(key: string, segmentId: string, end: 'from' | 'to') {
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push({ segmentId, end });
    }
    for (const segment of segments) {
      for (const [ep, end] of [[segment.from, 'from'], [segment.to, 'to']] as const) {
        addKey(`${ep.entityId}:${ep.side}`, segment.id, end);
      }
    }
    return { connectorSegmentsMap: map, connectorHasEdge: new Set(map.keys()) };
  }, [segments]);

  // ── Selected edge connectors (replaces useSelectedEdgeConnectors per component) ──
  const segmentIdSet = useMemo(() => new Set(segments.map(s => s.id)), [segments]);

  const selectedSegmentIdsKey = useSelector(
    canvasSelectionStore,
    s => {
      const matches: string[] = [];
      for (const id of s.context.selectedEntityIds) {
        if (segmentIdSet.has(id)) matches.push(id);
      }
      return matches.join(',');
    },
  );

  const selectedEdgeConnectors = useMemo(() => {
    if (!selectedSegmentIdsKey) return EMPTY_SET;
    const set = new Set<string>();
    for (const segmentId of selectedSegmentIdsKey.split(',')) {
      const segment = segments.find(s => s.id === segmentId);
      if (segment) {
        for (const ep of [segment.from, segment.to]) {
          set.add(`${ep.entityId}:${ep.side}`);
        }
      }
    }
    return set;
  }, [selectedSegmentIdsKey, segments]);

  // ── Group redirect (replaces useGroupRedirect per component) ──
  const enteredGroupId = useSelector(canvasSelectionStore, s => s.context.enteredGroupId);

  const getRedirectGroupId = useMemo(() => {
    const parentMap = new Map<string, string>();
    const groupMap = new Map(groups.map(g => [g.id, g]));
    for (const g of groups) {
      for (const mid of g.memberEntityIds) {
        parentMap.set(mid, g.id);
      }
    }
    return (entityId: string): string | null => {
      return resolveGroupRedirect(entityId, enteredGroupId, parentMap, groupMap);
    };
  }, [groups, enteredGroupId]);

  // ── Context menus (called ONCE here instead of per entity component) ──
  const { handleEntityContextMenu, handleGroupToggleCollapse } = useCanvasContextMenus();

  const value = useMemo<EntityDataContextValue>(() => ({
    connectorHasEdge,
    connectorSegmentsMap,
    selectedEdgeConnectors,
    getRedirectGroupId,
    handleEntityContextMenu,
    handleGroupToggleCollapse,
  }), [connectorHasEdge, connectorSegmentsMap, selectedEdgeConnectors, getRedirectGroupId, handleEntityContextMenu, handleGroupToggleCollapse]);

  return (
    <EntityDataCtx.Provider value={value}>
      {children}
    </EntityDataCtx.Provider>
  );
}
